interface DestroyParams<
  OldItemsT extends { id?: number },
  FormItemsT extends Record<string, any>,
> {
  oldItems: OldItemsT[];
  formItems: FormItemsT[];
}

export function setDestroyItems<
  OldItemsT extends { id?: number },
  FormItemsT extends Record<string, any>,
>({
  oldItems,
  formItems,
}: DestroyParams<OldItemsT, FormItemsT>): (FormItemsT | { id: number } & { _destroy: true })[] {
  const removeItemsHash: Record<string, boolean> = {};

  const newItems: (FormItemsT | { id: number } & { _destroy: true })[] = [...formItems];

  oldItems.forEach(item => {
    if (item?.id) removeItemsHash[item.id] = true;
  });
  newItems.forEach(item => {
    if ('id' in item && item.id) {
      delete removeItemsHash[item.id];
    }
  });

  Object.keys(removeItemsHash).forEach(id => {
    const removedItem = {
      id: Number.parseInt(id, 10),
      _destroy: true
    } as Pick<FormItemsT, 'id'> & { _destroy: true };
    newItems.push(removedItem);
  });

  return removeEmptyFields(newItems);
}

type RemoveEmptyFields<T> = T extends Array<infer U>
  ? NonNullable<U>[]
  : T extends Record<string, any>
  ? { [K in keyof T as T[K] extends null | undefined | '' ? never : K]: T[K] }
  : T;

export const removeEmptyFields = <T>(values: T): RemoveEmptyFields<T> => {
  const isArray = Array.isArray(values);
  const result = isArray ? [] : {};
  if (typeof values === 'object' && values !== null) {
    for (const key in values) {
      const value = values[key];
      if (value || value === 0 || typeof value === 'boolean') {
        if (isArray) {
          (result as any[]).push(value);
        } else {
          (result as Record<string, any>)[key] = value;
        }
      }
    }
    return result as RemoveEmptyFields<T>;
  }
  return values as RemoveEmptyFields<T>;
};

export const setValues = (object: any) => {
  if (object && typeof object !== 'string') {
    Object.keys(object).forEach(key => {
      if (Array.isArray(object[key])) {
        object[key] = object[key][0];
      } else setValues(object[key]);
    });
  }
};

export const getFieldName = (baseName: string | undefined, ...parts: string[]): string => {
  if (!baseName) return parts.join('.');
  return [baseName, ...parts].join('.');
};

export function setEntityDestroyParams<
  OldItemT,
  newItemT,
>({
  oldItem,
  newItem,
}: {
  oldItem: OldItemT;
  newItem: newItemT;
}): newItemT | { _destroy: true } {
  if (oldItem && !newItem) return {
    _destroy: true
  };

  return newItem;
}

/**
 * Generates a type for form field names that contains all possible paths to nested field names in format field1.field2.0.field3. 
 * The depth of nesting is limited to 10 levels to prevent infinite recursion.
 * @example
 * type FormValues = {
 *   user: {
 *     name: string;
 *     addresses: {
 *       street: string;
 *       city: string;
 *     }[];
 *   };
 * };
 *
 * type FieldNames = RecursiveKeys<FormValues>;
 * // FieldNames will be "user" | "user.name" | "user.addresses" | "user.addresses.0" | "user.addresses.0.street" | "user.addresses.0.city"
 */
export type RecursiveKeys<T, Depth extends number[] = []> = Depth['length'] extends 10
  ? never
  : T extends (infer E)[]
  ? `${number}` | `${number}.${RecursiveKeys<E, [...Depth, 0]>}`
  : T extends object
  ? {
    [K in keyof T & string]: K | `${K}.${RecursiveKeys<T[K], [...Depth, 0]>}`;
  }[keyof T & string]
  : never;

/**
 * Given a type T and a dot-separated path string K (a RecursiveKeys<T>),
 * resolves the type of the value at that path.
 *
 * Uses mapped-type coercion (`${MK}`) so that enum-member computed property keys
 * (e.g. `[SHIPMENT_DELIVERY_TYPE.truck]`) are matched against plain string path
 * segments the same way RecursiveKeys produces them.
 *
 * @example
 * type FormValues = { user: { name: string; addresses: { street: string }[] } };
 * type V1 = RecursiveValues<FormValues, 'user'>;              // { name: string; addresses: ... }
 * type V2 = RecursiveValues<FormValues, 'user.name'>;         // string
 * type V3 = RecursiveValues<FormValues, 'user.addresses.0'>;  // { street: string }
 */
export type RecursiveValues<T, K extends string> =
  T extends (infer E)[]
    ? K extends `${number}`
      ? E
      : K extends `${number}.${infer Tail}`
        ? RecursiveValues<E, Tail>
        : never
    : K extends `${infer Head}.${infer Tail}`
      ? { [MK in keyof T & string]: `${MK}` extends Head ? RecursiveValues<T[MK], Tail> : never }[keyof T & string]
      : { [MK in keyof T & string]: `${MK}` extends `${K}` ? T[MK] : never }[keyof T & string];

type FormFieldNameGenerator<FormValues> = {
  n: <K extends RecursiveKeys<FormValues>>(field: K) => K;
};

export const getFormFieldNameGenerator = <FormValues>(): FormFieldNameGenerator<FormValues> => {
  return {
    n: <K extends RecursiveKeys<FormValues>>(field: K) => field
  };
};